import * as Tone from "tone";
import {
  MIDI_DRUM_CHANNEL,
  addMidiNote,
  addMidiTrack,
  createMidiDocument,
  downloadMidi,
  rememberMidiExportNoticeDismissed,
  shouldShowMidiExportNotice,
} from "../js/music/midi-export.js";
import { playSoundfontPreview } from "../js/music/soundfont-previews.js";

const STORAGE_KEY = "bardsCorner.drums.v1";
const PPQ = 960;
const CLIP_EDITOR_HANDOFF_KEY = "bardsCorner.clipEditor.handoff.v1";
const CLIP_EDITOR_RETURN_KEY = "bardsCorner.clipEditor.return.v1";
const DEFAULT_TITLE = "Untitled Drum Pattern";
const DEFAULT_STEPS_PER_BAR = 16;
const DEFAULT_BASE_MIDI = 35;
const DEFAULT_GRID_OCTAVES = 2;
const DEFAULT_KIT_SIZE = "medium";
const DEFAULT_SUSTAIN = 0.75;
const MAX_SUSTAIN_SECONDS = 1.6;
const GRID_RESOLUTIONS = [
  { stepsPerBar: 12, label: "12/8" },
  { stepsPerBar: 16, label: "1/16" },
  { stepsPerBar: 24, label: "1/16T" },
  { stepsPerBar: 32, label: "1/32" },
  { stepsPerBar: 48, label: "1/32T" },
];
const KIT_SIZES = ["small", "medium", "large", "huge"];
const MAX_BARS = 16;
const MIN_BPM = 40;
const MAX_BPM = 240;
const PLAYBACK_START_DELAY_SECONDS = 0.08;
const SCHEDULE_LOOKAHEAD_SECONDS = 0.12;
const NORMAL_ACCENT = "normal";
const LOUD_ACCENT = "accent";
const GHOST_ACCENT = "ghost";
const ACCENT_VELOCITIES = {
  [GHOST_ACCENT]: 0.34,
  [NORMAL_ACCENT]: 0.68,
  [LOUD_ACCENT]: 0.95,
};
const noteColors = ["#c73528", "#c7771f", "#a3a226", "#4fad27", "#2d92a8", "#8c22b4", "#b821b8"];
const FULL_DRUM_MIDI_NOTES = Array.from({ length: 47 }, (_unused, index) => DEFAULT_BASE_MIDI + index);
const ROLAND_606_MIDI_NOTES = [35, 36, 38, 42, 44, 45, 46, 49, 50];
const ROLAND_707_MIDI_NOTES = [35, 36, 38, 39, 40, 42, 44, 45, 46, 49, 50, 51, 54, 56];
const ROLAND_808_MIDI_NOTES = [35, 36, 38, 39, 40, 41, 42, 44, 45, 46, 47, 48, 49, 50, 51, 54, 55, 56, 57, 59, 70, 75];
const ROLAND_909_MIDI_NOTES = [35, 36, 38, 39, 40, 42, 44, 45, 46, 47, 49, 50, 51];
const CASIO_VL_1_MIDI_NOTES = [35, 36, 38, 40, 42, 45, 50];
const LINNDRUM_MIDI_NOTES = [35, 36, 37, 38, 39, 42, 44, 45, 46, 47, 49, 50, 51, 54, 56, 63, 64, 69];
const DRUM_SOUNDFONTS = [
  { id: "drums", label: "Drums", globalName: "drums", gain: 0.9, defaultVelocity: 0.86, release: 0.2, availableMidiNotes: FULL_DRUM_MIDI_NOTES },
  { id: "casio_vl_1", label: "Casio VL-1", globalName: "drums_casio_vl_1", gain: 0.9, defaultVelocity: 0.86, release: 0.2, availableMidiNotes: CASIO_VL_1_MIDI_NOTES },
  { id: "linn_linndrum", label: "LinnDrum", globalName: "drums_linn_linndrum", gain: 0.9, defaultVelocity: 0.86, release: 0.2, availableMidiNotes: LINNDRUM_MIDI_NOTES },
  { id: "roland_606", label: "Roland TR-606", globalName: "drums_roland_606", gain: 0.9, defaultVelocity: 0.86, release: 0.2, availableMidiNotes: ROLAND_606_MIDI_NOTES },
  { id: "roland_707", label: "Roland TR-707", globalName: "drums_roland_707", gain: 0.9, defaultVelocity: 0.86, release: 0.2, availableMidiNotes: ROLAND_707_MIDI_NOTES },
  { id: "roland_808", label: "Roland TR-808", globalName: "drums_roland_808", gain: 0.9, defaultVelocity: 0.86, release: 0.2, availableMidiNotes: ROLAND_808_MIDI_NOTES },
  { id: "roland_909", label: "Roland TR-909", globalName: "drums_roland_909", gain: 0.9, defaultVelocity: 0.86, release: 0.2, availableMidiNotes: ROLAND_909_MIDI_NOTES },
];
const GM_DRUM_NAMES = {
  35: "Acoustic Bass Drum",
  36: "Electric Bass Drum",
  37: "Side Stick",
  38: "Acoustic Snare",
  39: "Hand Clap",
  40: "Electric Snare",
  41: "Low Floor Tom",
  42: "Closed Hi-Hat",
  43: "High Floor Tom",
  44: "Pedal Hi-Hat",
  45: "Low Tom",
  46: "Open Hi-Hat",
  47: "Low-Mid Tom",
  48: "High-Mid Tom",
  49: "Crash Cymbal 1",
  50: "High Tom",
  51: "Ride Cymbal 1",
  52: "Chinese Cymbal",
  53: "Ride Bell",
  54: "Tambourine",
  55: "Splash Cymbal",
  56: "Cowbell",
  57: "Crash Cymbal 2",
  58: "Vibraslap",
  59: "Ride Cymbal 2",
  60: "High Bongo",
  61: "Low Bongo",
  62: "Mute High Conga",
  63: "Open High Conga",
  64: "Low Conga",
  65: "High Timbale",
  66: "Low Timbale",
  67: "High Agogô",
  68: "Low Agogô",
  69: "Cabasa",
  70: "Maracas",
  71: "Short Whistle",
  72: "Long Whistle",
  73: "Short Güiro",
  74: "Long Güiro",
  75: "Claves",
  76: "High Woodblock",
  77: "Low Woodblock",
  78: "Mute Cuíca",
  79: "Open Cuíca",
  80: "Mute Triangle",
  81: "Open Triangle",
};
const SMALL_KIT_MIDI_NOTES = new Set([36, 38, 37, 39, 49, 46, 42]);
const MEDIUM_KIT_MIDI_NOTES = new Set([
  ...SMALL_KIT_MIDI_NOTES,
  45,
  47,
  50,
  56,
  64,
  63,
  62,
  75,
  70,
]);
const LEGACY_DRUM_LANE_MIDI_NOTES = [
  36,
  38,
  45,
  47,
  50,
  37,
  39,
  56,
  49,
  46,
  42,
  64,
  63,
  62,
  75,
  70,
  35,
  40,
  44,
  51,
  53,
  54,
  55,
  58,
];
const DRUM_LANES = Array.from({ length: 47 }, (_, semitone) => {
  const midiNote = DEFAULT_BASE_MIDI + semitone;
  const kits = ["huge"];

  if (midiNote <= 58) {
    kits.push("large");
  }

  if (MEDIUM_KIT_MIDI_NOTES.has(midiNote)) {
    kits.push("medium");
  }

  if (SMALL_KIT_MIDI_NOTES.has(midiNote)) {
    kits.push("small");
  }

  return {
    label: GM_DRUM_NAMES[midiNote] || `Drum ${midiNote}`,
    midiNote,
    kits,
  };
});

const els = {
  fileNewButton: document.getElementById("fileNewButton"),
  fileLoadButton: document.getElementById("fileLoadButton"),
  fileSaveButton: document.getElementById("fileSaveButton"),
  fileSaveAsButton: document.getElementById("fileSaveAsButton"),
  fileShareButton: document.getElementById("fileShareButton"),
  fileExportMidiButton: document.getElementById("fileExportMidiButton"),
  clipTitleInput: document.getElementById("clipTitleInput"),
  originBardIndicator: document.getElementById("originBardIndicator"),
  clipStats: document.getElementById("clipStats"),
  unsavedChangesIndicator: document.getElementById("unsavedChangesIndicator"),
  playButton: document.getElementById("playButton"),
  doubleButton: document.getElementById("doubleButton"),
  backToProgressionButton: document.getElementById("backToProgressionButton"),
  clipSettingsButton: document.getElementById("clipSettingsButton"),
  bpmInput: document.getElementById("bpmInput"),
  bpmDisplay: document.getElementById("bpmDisplay"),
  tapBpmButton: document.getElementById("tapBpmButton"),
  soundBankSelect: document.getElementById("soundBankSelect"),
  kitSizeField: document.getElementById("kitSizeField"),
  kitSizeSelect: document.getElementById("kitSizeSelect"),
  sustainInput: document.getElementById("sustainInput"),
  saveClipModal: document.getElementById("saveClipModal"),
  saveClipNameInput: document.getElementById("saveClipNameInput"),
  saveClipDuplicateMessage: document.getElementById("saveClipDuplicateMessage"),
  saveSharedCheckbox: document.getElementById("saveSharedCheckbox"),
  saveSharedInfo: document.getElementById("saveSharedInfo"),
  saveStockCheckbox: document.getElementById("saveStockCheckbox"),
  saveStockCheckboxWrap: document.getElementById("saveStockCheckboxWrap"),
  saveExistingClipList: document.getElementById("saveExistingClipList"),
  saveClipMessage: document.getElementById("saveClipMessage"),
  confirmSaveClipButton: document.getElementById("confirmSaveClipButton"),
  shareModal: document.getElementById("shareModal"),
  shareLinkHelp: document.getElementById("shareLinkHelp"),
  shareLinkInput: document.getElementById("shareLinkInput"),
  shareLinkMessage: document.getElementById("shareLinkMessage"),
  copyShareLinkButton: document.getElementById("copyShareLinkButton"),
  midiExportModal: document.getElementById("midiExportModal"),
  confirmMidiExportButton: document.getElementById("confirmMidiExportButton"),
  midiExportNeverShowButton: document.getElementById("midiExportNeverShowButton"),
  clipGrid: document.getElementById("clipGrid"),
  clipSettingsModal: document.getElementById("clipSettingsModal"),
  gridResolution16Radio: document.getElementById("gridResolution16Radio"),
  loadClipModal: document.getElementById("loadClipModal"),
  loadClipMessage: document.getElementById("loadClipMessage"),
  loadClipList: document.getElementById("loadClipList"),
  loadClipOwnedTab: document.getElementById("loadClipOwnedTab"),
  loadClipSharedTab: document.getElementById("loadClipSharedTab"),
  loadClipOwnerSortButton: document.getElementById("loadClipOwnerSortButton"),
  confirmLoadClipButton: document.getElementById("confirmLoadClipButton"),
  noteAccentModal: document.getElementById("noteAccentModal"),
  noteAccentTitle: document.getElementById("noteAccentTitle"),
  noteAccentRemoveButton: document.getElementById("noteAccentRemoveButton"),
  laneSettingsModal: document.getElementById("laneSettingsModal"),
  laneSettingsTitle: document.getElementById("laneSettingsTitle"),
  laneVolumeInput: document.getElementById("laneVolumeInput"),
  laneToneInput: document.getElementById("laneToneInput"),
  laneDecayInput: document.getElementById("laneDecayInput"),
  lanePitchStepsInput: document.getElementById("lanePitchStepsInput"),
  lanePitchCentsInput: document.getElementById("lanePitchCentsInput"),
  laneMidiNoteSelect: document.getElementById("laneMidiNoteSelect"),
  lanePreviewButton: document.getElementById("lanePreviewButton"),
};

const state = {
  clip: createDefaultClip(),
  saveTimer: null,
  serverClipId: null,
  savingClip: false,
  loadingClips: false,
  loadingSaveClips: false,
  loadModalClips: [],
  loadModalMode: "owned",
  loadModalSortKey: "updatedAt",
  loadModalSortDirection: "desc",
  loadModalSelectedClipId: null,
  loadPreviewTimeoutIds: [],
  loadPreviewRequestId: null,
  saveModalClips: [],
  saveModalSelectedClipId: null,
  saveModalDuplicateClipId: null,
  saveModalSortKey: "updatedAt",
  saveModalSortDirection: "desc",
  serverClipSignature: null,
  loadedOriginBardName: "",
  loadedClipStats: null,
  serverClipLocked: false,
  currentUser: null,
  returnHandoff: null,
  playing: false,
  playbackStartedAt: 0,
  playheadAnimationFrame: null,
  laneSamplers: new Map(),
  scheduledIds: [],
  soundBankChangeToken: 0,
  longPressTimer: null,
  longPressHandled: false,
  selectedNoteCell: null,
  selectedLaneSemitone: null,
  tapTimes: [],
  lastKitSize: DEFAULT_KIT_SIZE,
  pristineUnsavedClipSignature: null,
  audioWarmPromise: null,
};

function makeId(prefix) {
  if (window.crypto?.randomUUID) {
    return `${prefix}_${window.crypto.randomUUID()}`;
  }

  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function createDefaultClip(kitSize = DEFAULT_KIT_SIZE) {
  const now = new Date().toISOString();

  return {
    schema: "ClipJSON",
    version: 1,
    id: makeId("clip"),
    title: DEFAULT_TITLE,
    bars: 1,
    stepsPerBar: DEFAULT_STEPS_PER_BAR,
    bpm: 120,
    soundBank: "drums",
    kitSize: validKitSize(kitSize),
    sustain: DEFAULT_SUSTAIN,
    key: "C",
    baseOctave: 0,
    gridOctaves: DEFAULT_GRID_OCTAVES,
    quality: "major",
    showChromatic: true,
    shared: false,
    stock: false,
    clipType: "drum",
    laneSettings: defaultLaneSettings(),
    notes: [],
    events: [],
    createdAt: now,
    updatedAt: now,
  };
}

function defaultLaneSettings() {
  return Object.fromEntries(Array.from({ length: DRUM_LANES.length }, (_unused, semitone) => [
    `${semitone}`,
    createDefaultLaneSetting(semitone),
  ]));
}

function createDefaultLaneSetting(semitone) {
  const lane = DRUM_LANES[semitone];
  return {
    muted: false,
    volume: 0.85,
    tone: 0.72,
    decay: 0.55,
    pitchSteps: 0,
    pitchCents: 0,
    midiNote: lane?.midiNote || DEFAULT_BASE_MIDI + semitone,
  };
}

function clampNumber(value, min, max, fallback) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return fallback;
  }

  return Math.min(max, Math.max(min, number));
}

function normalizeClip(value, fallbackKitSize = DEFAULT_KIT_SIZE) {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const bars = [1, 2, 4, 8, 16].includes(Number(source.bars)) ? Number(source.bars) : 1;
  const stepsPerBar = validStepsPerBar(source.stepsPerBar ?? source.steps_per_bar ?? source.gridResolution ?? source.grid_resolution);
  const soundBank = normalizeSoundfontId(source.soundBank || source.sound_bank || source.instrumentName);
  const now = new Date().toISOString();

  const clip = {
    schema: "ClipJSON",
    version: 1,
    id: String(source.id || makeId("clip")),
    title: String(source.title || DEFAULT_TITLE).slice(0, 160),
    bars,
    stepsPerBar,
    bpm: Math.trunc(clampNumber(source.bpm, MIN_BPM, MAX_BPM, 120)),
    soundBank,
    kitSize: isDrumMachineSoundfont(soundBank) ? "huge" : validKitSize(source.kitSize || source.kit_size, fallbackKitSize),
    sustain: clampNumber(source.sustain, 0, 1, DEFAULT_SUSTAIN),
    key: "C",
    baseOctave: 0,
    gridOctaves: DEFAULT_GRID_OCTAVES,
    quality: "major",
    showChromatic: true,
    shared: Boolean(source.shared),
    stock: Boolean(source.stock),
    clipType: "drum",
    laneSettings: normalizeLaneSettings(source.laneSettings || source.lane_settings),
    notes: normalizeNotes(source.notes, bars, stepsPerBar),
    events: [],
    createdAt: source.createdAt || now,
    updatedAt: source.updatedAt || now,
  };
  syncClipEvents(clip);
  return clip;
}

function validStepsPerBar(value, fallback = DEFAULT_STEPS_PER_BAR) {
  const stepsPerBar = Math.trunc(Number(value));
  return GRID_RESOLUTIONS.some((resolution) => resolution.stepsPerBar === stepsPerBar)
    ? stepsPerBar
    : fallback;
}

function validKitSize(value, fallback = DEFAULT_KIT_SIZE) {
  const kitSize = String(value || "").trim().toLowerCase();
  return KIT_SIZES.includes(kitSize) ? kitSize : fallback;
}

function normalizeSoundfontId(value, fallback = "drums") {
  const raw = String(value || "").trim();
  return DRUM_SOUNDFONTS.some((config) => config.id === raw) ? raw : fallback;
}

function isDrumMachineSoundfont(value) {
  return normalizeSoundfontId(value) !== "drums";
}

function effectiveKitSizeForSoundBank(soundBank, kitSize) {
  return isDrumMachineSoundfont(soundBank) ? "huge" : validKitSize(kitSize);
}

function soundfontConfig(id) {
  return DRUM_SOUNDFONTS.find((config) => config.id === id) || DRUM_SOUNDFONTS[0];
}

function labelForSoundBank(value) {
  return soundfontConfig(normalizeSoundfontId(value)).label;
}

function availableMidiNotesForSoundfont(value = state.clip.soundBank) {
  const config = soundfontConfig(normalizeSoundfontId(value));
  return new Set(config.availableMidiNotes || FULL_DRUM_MIDI_NOTES);
}

function laneMidiForClip(clip, semitone) {
  return Math.trunc(laneSettingForClip(clip, semitone).midiNote);
}

function isLaneAvailableForClip(clip, semitone, soundBank = clip.soundBank, kitSize = clip.kitSize) {
  const lane = DRUM_LANES[semitone];
  if (!lane || !lane.kits.includes(effectiveKitSizeForSoundBank(soundBank, kitSize))) {
    return false;
  }
  return availableMidiNotesForSoundfont(soundBank).has(laneMidiForClip(clip, semitone));
}

function normalizeLaneSettings(settings) {
  const normalized = defaultLaneSettings();
  const source = settings && typeof settings === "object" && !Array.isArray(settings) ? settings : {};

  for (let semitone = 0; semitone < DRUM_LANES.length; semitone += 1) {
    const lane = source[semitone] || source[`${semitone}`] || {};
    const sourceMidiNote = lane.midiNote ?? lane.midi_note;
    const defaultMidiNote = normalized[semitone].midiNote;
    const migratedMidiNote = Number(sourceMidiNote) === LEGACY_DRUM_LANE_MIDI_NOTES[semitone]
      ? undefined
      : sourceMidiNote;
    normalized[semitone] = {
      muted: Boolean(lane.muted),
      volume: clampNumber(lane.volume, 0, 1, normalized[semitone].volume),
      tone: clampNumber(lane.tone, 0, 1, normalized[semitone].tone),
      decay: clampNumber(lane.decay, 0, 1, normalized[semitone].decay),
      pitchSteps: Math.trunc(clampNumber(lane.pitchSteps ?? lane.pitch_steps, -24, 24, 0)),
      pitchCents: Math.trunc(clampNumber(lane.pitchCents ?? lane.pitch_cents, -100, 100, 0)),
      midiNote: Math.trunc(clampNumber(migratedMidiNote, 35, 81, defaultMidiNote)),
    };
  }

  return normalized;
}

function normalizeNotes(notes, bars, stepsPerBar = state.clip.stepsPerBar || DEFAULT_STEPS_PER_BAR) {
  if (!Array.isArray(notes)) {
    return [];
  }

  const totalSteps = bars * stepsPerBar;
  const seen = new Set();

  return notes.reduce((normalized, note) => {
    const step = Math.trunc(Number(note?.step));
    const semitone = Math.trunc(Number(note?.semitone));
    const accent = normalizeAccent(note?.accent);

    if (
      !Number.isInteger(step)
      || step < 0
      || step >= totalSteps
      || !Number.isInteger(semitone)
      || semitone < 0
      || semitone >= DRUM_LANES.length
      || seen.has(`${step}:${semitone}`)
    ) {
      return normalized;
    }

    seen.add(`${step}:${semitone}`);
    normalized.push({
      id: String(note.id || makeId("note")),
      step,
      semitone,
      degree: semitone % 12,
      durationSteps: 1,
      accent,
    });

    return normalized;
  }, []).sort((a, b) => a.step - b.step || a.semitone - b.semitone);
}

function normalizeAccent(value) {
  return [GHOST_ACCENT, NORMAL_ACCENT, LOUD_ACCENT].includes(value) ? value : NORMAL_ACCENT;
}

function readStoredState() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readClipEditorHandoff() {
  try {
    const raw = window.localStorage.getItem(CLIP_EDITOR_HANDOFF_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function loadStoredState() {
  const stored = readStoredState();
  state.lastKitSize = validKitSize(stored?.settings?.lastKitSize, DEFAULT_KIT_SIZE);
  state.pristineUnsavedClipSignature = localClipSignature(createDefaultClip(state.lastKitSize));

  const handoff = readClipEditorHandoff();
  if (handoff?.clip && handoff?.eventId) {
    state.clip = normalizeClip(handoff.clip, state.lastKitSize);
    state.lastKitSize = state.clip.kitSize;
    state.serverClipId = null;
    state.serverClipSignature = null;
    state.serverClipLocked = false;
    state.loadedOriginBardName = "";
    state.loadedClipStats = null;
    state.returnHandoff = {
      returnUrl: handoff.returnUrl || "/songs",
      trackId: String(handoff.trackId || ""),
      eventId: String(handoff.eventId),
    };
    try {
      window.localStorage.removeItem(CLIP_EDITOR_HANDOFF_KEY);
    } catch {
      // Handoff cleanup is best-effort.
    }
    saveState();
    return;
  }

  if (!stored) {
    return;
  }

  state.clip = normalizeClip(stored.clip, state.lastKitSize);
  state.lastKitSize = state.clip.kitSize;
  state.serverClipId = stored.settings?.serverClipId ? String(stored.settings.serverClipId) : null;
  state.serverClipSignature = stored.settings?.serverClipSignature
    ? String(stored.settings.serverClipSignature)
    : null;
  state.serverClipLocked = Boolean(stored.settings?.serverClipLocked);
  state.loadedClipStats = normalizeLoadedClipStats(stored.settings?.loadedClipStats);
  state.loadedOriginBardName = state.loadedClipStats?.bardName || "";
  state.returnHandoff = stored.settings?.returnHandoff?.eventId
    ? {
      returnUrl: stored.settings.returnHandoff.returnUrl || "/songs",
      trackId: String(stored.settings.returnHandoff.trackId || ""),
      eventId: String(stored.settings.returnHandoff.eventId),
    }
    : null;
}

function scheduleStateSave() {
  if (state.saveTimer) {
    window.clearTimeout(state.saveTimer);
  }
  state.saveTimer = window.setTimeout(saveState, 150);
}

function saveState() {
  if (state.saveTimer) {
    window.clearTimeout(state.saveTimer);
    state.saveTimer = null;
  }
  syncClipEvents();

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version: 1,
      savedAt: new Date().toISOString(),
      clip: state.clip,
      settings: {
        serverClipId: state.serverClipId,
        serverClipSignature: state.serverClipSignature,
        serverClipLocked: state.serverClipLocked,
        loadedClipStats: state.loadedClipStats,
        returnHandoff: state.returnHandoff,
        lastKitSize: state.lastKitSize,
      },
    }));
  } catch {
    // Storage can be unavailable or full; the editor should keep working.
  }
}

function touchClip() {
  syncClipEvents();
  state.clip.updatedAt = new Date().toISOString();
  scheduleStateSave();
  renderFileMenuState();
}

function syncClipEvents(clip = state.clip) {
  syncNoteMetadata(clip);
  clip.events = eventsForClip(clip);
}

function syncNoteMetadata(clip = state.clip) {
  clip.notes.forEach((note) => {
    const setting = laneSettingForClip(clip, note.semitone);
    note.midi = Math.trunc(setting.midiNote);
    note.drumName = friendlyDrumName(setting.midiNote, note.semitone);
    note.durationSteps = 1;
  });
}

function eventsForClip(clip = state.clip) {
  const visibleSemitones = new Set(kitLaneSemitones(clip.kitSize, clip, clip.soundBank));
  return clip.notes.filter((note) => visibleSemitones.has(note.semitone)).map((note) => {
    const setting = laneSettingForClip(clip, note.semitone);
    return {
      offsetBeats: (note.step * 4) / clip.stepsPerBar,
      durationBeats: 4 / clip.stepsPerBar,
      midi: setting.midiNote,
      pitchCents: pitchOffsetCents(setting),
      drumName: friendlyDrumName(setting.midiNote, note.semitone),
      semitone: note.semitone,
      accent: normalizeAccent(note.accent),
      velocity: ACCENT_VELOCITIES[normalizeAccent(note.accent)],
    };
  });
}

function laneSettingForClip(clip, semitone) {
  const key = `${semitone}`;
  return clip.laneSettings?.[key] || createDefaultLaneSetting(semitone);
}

function serverComparableClip(clip) {
  return {
    title: clip.title,
    bars: clip.bars,
    stepsPerBar: clip.stepsPerBar,
    bpm: clip.bpm,
    soundBank: normalizeSoundfontId(clip.soundBank),
    kitSize: validKitSize(clip.kitSize),
    sustain: clampNumber(clip.sustain, 0, 1, DEFAULT_SUSTAIN),
    key: "C",
    baseOctave: 0,
    gridOctaves: DEFAULT_GRID_OCTAVES,
    quality: "major",
    showChromatic: true,
    shared: Boolean(clip.shared),
    stock: Boolean(clip.stock),
    clipType: "drum",
    laneSettings: clip.laneSettings,
    notes: clip.notes,
    events: clip.events,
  };
}

function localComparableClip(clip, options = {}) {
  const comparable = {
    title: normalizeComparableTitle(clip.title),
    bars: clip.bars,
    stepsPerBar: clip.stepsPerBar,
    bpm: clip.bpm,
    soundBank: normalizeSoundfontId(clip.soundBank),
    kitSize: validKitSize(clip.kitSize),
    sustain: clampNumber(clip.sustain, 0, 1, DEFAULT_SUSTAIN),
    shared: Boolean(clip.shared),
    stock: Boolean(clip.stock),
    laneSettings: clip.laneSettings,
    notes: clip.notes,
  };

  if (options.ignoreKitSelection) {
    comparable.soundBank = "drums";
    comparable.kitSize = DEFAULT_KIT_SIZE;
  }

  return comparable;
}

function localClipSignature(clip = state.clip, options = {}) {
  return JSON.stringify(localComparableClip(clip, options));
}

function clipServerSignature(clip = state.clip) {
  return JSON.stringify(serverComparableClip(clip));
}

function isSavedServerClipDirty() {
  return Boolean(state.serverClipId && state.serverClipSignature && clipServerSignature() !== state.serverClipSignature);
}

function isUnsavedNewClipModified() {
  const pristineClip = createDefaultClip(state.lastKitSize);
  const pristineSignature = state.pristineUnsavedClipSignature || localClipSignature(pristineClip);
  if (localClipSignature() === pristineSignature) {
    return false;
  }

  if (state.clip.notes.length > 0) {
    return true;
  }

  return localClipSignature(state.clip, { ignoreKitSelection: true }) !== localClipSignature(pristineClip, { ignoreKitSelection: true });
}

function hasUnsavedClipChanges() {
  return state.serverClipId ? isSavedServerClipDirty() : isUnsavedNewClipModified();
}

function normalizeLoadedClipStats(clip) {
  if (!clip || typeof clip !== "object") {
    return null;
  }

  const id = clip.id ? String(clip.id) : "";
  if (!id) {
    return null;
  }

  return {
    id,
    shared: Boolean(clip.shared),
    stock: Boolean(clip.stock),
    usedCount: Math.max(0, Math.trunc(Number(clip.usedCount) || 0)),
    bardName: String(clip.bardName || "").trim(),
  };
}

function confirmDiscardUnsavedClip(action) {
  if (!hasUnsavedClipChanges()) {
    return true;
  }

  return window.confirm(`${action}? Unsaved drum pattern changes will be lost.`);
}

async function apiRequest(path, options = {}) {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });
  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(body.error || "The server could not complete that request.");
    error.status = response.status;
    throw error;
  }

  return body;
}

async function loadCurrentUser() {
  try {
    const body = await apiRequest("/api/auth/me");
    state.currentUser = body.user || null;
  } catch {
    state.currentUser = null;
  }

  renderAdminControls();
}

function renderAdminControls() {
  const isAdmin = Boolean(state.currentUser?.isAdmin);
  els.saveStockCheckboxWrap.classList.toggle("d-none", !isAdmin);
  els.saveStockCheckbox.disabled = !isAdmin;
  if (!isAdmin) {
    els.saveStockCheckbox.checked = false;
  }
}

async function showSaveClipModal() {
  const modal = window.bootstrap.Modal.getOrCreateInstance(els.saveClipModal);
  state.saveModalSelectedClipId = state.serverClipLocked ? null : state.serverClipId;
  const currentTitle = state.clip.title || DEFAULT_TITLE;
  els.saveClipNameInput.value = state.serverClipLocked ? `${currentTitle} Copy`.slice(0, 160) : currentTitle;
  els.saveSharedCheckbox.checked = Boolean(state.clip.shared);
  renderSaveSharedInfo();
  els.saveStockCheckbox.checked = Boolean(state.currentUser?.isAdmin && state.clip.stock);
  state.loadingSaveClips = true;
  renderSaveClipList([], "Loading saved drum patterns...");
  renderSaveModalDuplicateState();
  renderSaveModalState();
  modal.show();

  try {
    const body = await apiRequest("/api/clips?mode=my&includeClip=1");
    state.saveModalClips = (body.clips || []).filter((clip) => clip.clipType === "drum");
    renderSaveClipList(state.saveModalClips);
    renderSaveModalDuplicateState();
  } catch (error) {
    state.saveModalClips = [];
    renderSaveClipList([], error.message || "Could not load saved drum patterns.");
  } finally {
    state.loadingSaveClips = false;
    renderSaveModalState();
  }
}

async function saveClip() {
  if (!state.serverClipId) {
    await showSaveClipModal();
    return;
  }

  if (state.serverClipLocked) {
    await showSaveClipModal();
    return;
  }

  await saveClipToServer({
    title: state.clip.title,
    shared: state.clip.shared,
    stock: state.clip.stock,
    serverClipId: state.serverClipId,
  });
}

async function saveClipFromModal() {
  const duplicateClip = currentSaveDuplicateClip();
  let serverClipId = state.saveModalSelectedClipId;

  if (duplicateClip) {
    const overwrite = window.confirm(`Are you sure you want to save over "${duplicateClip.title || DEFAULT_TITLE}"?`);
    if (!overwrite) {
      return;
    }
    serverClipId = duplicateClip.id;
  }

  await saveClipToServer({
    title: els.saveClipNameInput.value,
    shared: els.saveSharedCheckbox.checked,
    stock: els.saveStockCheckbox.checked,
    serverClipId,
  });
}

async function saveClipToServer(options = {}) {
  if (state.savingClip) {
    return;
  }

  state.savingClip = true;
  renderFileMenuState();

  try {
    const selectedClip = options.serverClipId
      ? state.saveModalClips.find((clip) => clip.id === options.serverClipId)
      : null;
    if (selectedClip?.clipId) {
      state.clip.id = selectedClip.clipId;
    } else if (!options.serverClipId && state.serverClipId) {
      state.clip.id = makeId("clip");
    }
    state.clip.title = String(options.title ?? state.clip.title).trim().slice(0, 160) || DEFAULT_TITLE;
    state.clip.soundBank = normalizeSoundfontId(els.soundBankSelect.value);
    state.clip.shared = Boolean(options.shared ?? state.clip.shared);
    state.clip.stock = Boolean(options.stock ?? state.clip.stock);
    state.clip.clipType = "drum";
    syncClipEvents();
    const body = await apiRequest("/api/clips", {
      method: "POST",
      body: JSON.stringify({
        clipId: state.clip.id,
        title: state.clip.title,
        shared: state.clip.shared,
        stock: state.clip.stock,
        clipType: "drum",
        clipDuration: state.clip.bars,
        baseOctave: state.clip.baseOctave,
        gridOctaves: state.clip.gridOctaves,
        soundBank: state.clip.soundBank,
        clip: state.clip,
      }),
    });

    state.serverClipId = body.clip?.id ? String(body.clip.id) : state.serverClipId;
    state.serverClipLocked = Boolean(body.clip?.locked);
    state.loadedClipStats = normalizeLoadedClipStats(body.clip);
    state.loadedOriginBardName = state.loadedClipStats?.bardName || "";
    state.serverClipSignature = clipServerSignature();
    scheduleStateSave();
    render();
    window.bootstrap.Modal.getOrCreateInstance(els.saveClipModal).hide();
  } catch (error) {
    window.alert(error.message || "Save failed.");
  } finally {
    state.savingClip = false;
    renderFileMenuState();
  }
}

function renderSaveClipList(clips, message = "") {
  const filteredClips = sortClipsForList(
    clips.filter((clip) => clip.clipType === "drum"),
    state.saveModalSortKey,
    state.saveModalSortDirection,
  );
  els.saveClipMessage.textContent = message || (filteredClips.length ? "" : "No saved Drum clips yet.");
  els.saveExistingClipList.replaceChildren(...filteredClips.map((clip) => clipListButton(clip, () => selectSaveModalClip(clip.id), clip.id === state.saveModalSelectedClipId, {
    showPreview: true,
    showStats: true,
  })));
  renderSaveSortHeaders();
}

function clipListButton(clip, onClick, active = false, options = {}) {
  const button = document.createElement(options.asDiv ? "div" : "button");
  button.className = `list-group-item list-group-item-action bg-dark text-light border-secondary clip-list-row${active && !options.useSelectedClass ? " active" : ""}`;
  button.classList.toggle("is-selected", active && Boolean(options.useSelectedClass));
  button.classList.toggle("has-preview", Boolean(options.showPreview));
  if (options.asDiv) {
    button.setAttribute("role", "button");
    button.setAttribute("tabindex", "0");
  } else {
    button.type = "button";
  }
  button.dataset.clipId = clip.id;
  const sharedMode = options.ownerMode === "bard";
  const ownerCell = sharedMode
    ? `<a class="clip-list-cell text-secondary" href="${escapeHtml(leaderboardBardUrl(clip.bardName))}">${escapeHtml(clip.bardName || "Shared")}</a>`
    : `<span class="clip-list-cell text-secondary">${renderClipAccess(clip)}</span>`;
  button.innerHTML = `
    ${options.showPreview ? `<span class="clip-list-preview-cell">${renderClipMiniPreview(clip)}</span>` : ""}
    <span class="clip-list-title-wrap">
      <strong class="clip-list-cell clip-list-title-cell">${escapeHtml(clip.title || DEFAULT_TITLE)}</strong>
      ${options.showStats ? renderClipListStats(clip) : ""}
    </span>
    <span class="clip-list-cell">Drum</span>
    <span class="clip-list-cell text-secondary">${escapeHtml(formatClipDate(clip.updatedAt))}</span>
    ${ownerCell}
  `;
  button.querySelector("a")?.addEventListener("click", (event) => event.stopPropagation());
  button.addEventListener("click", onClick);
  if (options.asDiv) {
    button.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    });
  }
  return button;
}

function renderSaveSortHeaders() {
  document.querySelectorAll("[data-save-sort]").forEach((button) => {
    const active = button.dataset.saveSort === state.saveModalSortKey;
    button.classList.toggle("is-active", active);
    button.setAttribute("aria-sort", active ? (state.saveModalSortDirection === "asc" ? "ascending" : "descending") : "none");
  });
}

function selectSaveModalClip(clipId) {
  const clip = state.saveModalClips.find((candidate) => candidate.id === clipId);
  if (!clip) {
    return;
  }

  state.saveModalSelectedClipId = clip.id;
  els.saveClipNameInput.value = clip.title || DEFAULT_TITLE;
  els.saveSharedCheckbox.checked = Boolean(clip.shared);
  renderSaveSharedInfo();
  els.saveStockCheckbox.checked = Boolean(state.currentUser?.isAdmin && clip.stock);
  renderSaveClipList(state.saveModalClips);
  renderSaveModalDuplicateState();
}

function clearSaveModalSelectionIfEdited() {
  const selectedClip = state.saveModalClips.find((clip) => clip.id === state.saveModalSelectedClipId);
  if (!selectedClip) {
    return;
  }

  const selectedTitle = normalizeComparableTitle(selectedClip.title || DEFAULT_TITLE);
  const currentTitle = normalizeComparableTitle(els.saveClipNameInput.value || DEFAULT_TITLE);

  if (selectedTitle !== currentTitle) {
    state.saveModalSelectedClipId = null;
    renderSaveClipList(state.saveModalClips);
  }
}

function renderSaveModalDuplicateState() {
  const duplicateClip = currentSaveDuplicateClip();
  state.saveModalDuplicateClipId = duplicateClip?.id || null;
  els.saveClipNameInput.classList.toggle("is-duplicate", Boolean(duplicateClip));
  els.saveClipDuplicateMessage.textContent = duplicateClip ? "A Drum clip with this name already exists." : "";
}

function renderSaveSharedInfo() {
  els.saveSharedInfo.classList.toggle("d-none", !els.saveSharedCheckbox.checked);
}

function currentSaveDuplicateClip() {
  const title = normalizeComparableTitle(els.saveClipNameInput.value);
  if (!title) {
    return null;
  }

  return state.saveModalClips.find((clip) => clip.clipType === "drum" && normalizeComparableTitle(clip.title) === title) || null;
}

function renderSaveModalState() {
  els.confirmSaveClipButton.disabled = state.savingClip || state.loadingSaveClips;
  els.confirmSaveClipButton.textContent = state.savingClip ? "Saving..." : "OK";
}

function normalizeComparableTitle(value) {
  return String(value || "").trim().replace(/\s+/g, " ").toLowerCase();
}

function formatClipAccess(clip) {
  return [
    clip.shared ? "Public" : "",
    clip.stock ? "Stock" : "",
  ].filter(Boolean).join(", ") || "Private";
}

function isReadOnlyClip(clip) {
  return Boolean(clip.locked) || Number(clip.usedCount) > 0 || Number(clip.thumbsUp) > 0;
}

function renderClipAccess(clip) {
  const labels = [formatClipAccess(clip)];
  if (isReadOnlyClip(clip)) {
    labels.push("Read-only");
  }
  return labels.map((label) => `<span class="access-chip${label === "Read-only" ? " is-read-only" : ""}">${escapeHtml(label)}</span>`).join("");
}

function leaderboardBardUrl(bardName) {
  const params = new URLSearchParams({ bardName: String(bardName || "") });
  return `/leaderboard/?${params}`;
}

async function showLoadClipModal() {
  if (!confirmDiscardUnsavedClip("Open another drum pattern")) {
    return;
  }

  const modal = window.bootstrap.Modal.getOrCreateInstance(els.loadClipModal);
  state.loadingClips = true;
  state.loadModalSelectedClipId = null;
  cancelLoadClipPreview();
  renderLoadClipList([], "Loading saved drum patterns...");
  modal.show();

  try {
    const [ownedBody, sharedBody] = await Promise.all([
      apiRequest("/api/clips?mode=my&type=drum&includeClip=1"),
      apiRequest("/api/clips?mode=shared&type=drum&includeClip=1"),
    ]);
    state.loadModalClips = [
      ...(ownedBody.clips || []),
      ...(sharedBody.clips || []).filter((clip) => !clip.owned),
    ].filter((clip) => clip.clipType === "drum");
    renderLoadClipList(state.loadModalClips);
  } catch (error) {
    state.loadModalClips = [];
    renderLoadClipList([], error.message || "Could not load drum patterns.");
  } finally {
    state.loadingClips = false;
    renderLoadModalState();
  }
}

function renderLoadClipList(clips, message = "") {
  renderLoadModeTabs();
  const sharedMode = state.loadModalMode === "shared";
  if (sharedMode && state.loadModalSortKey === "access") {
    state.loadModalSortKey = "bardName";
  } else if (!sharedMode && state.loadModalSortKey === "bardName") {
    state.loadModalSortKey = "access";
  }
  if (els.loadClipOwnerSortButton) {
    els.loadClipOwnerSortButton.textContent = sharedMode ? "Bard" : "Access";
    els.loadClipOwnerSortButton.dataset.loadSort = sharedMode ? "bardName" : "access";
  }
  const sortedClips = sortClipsForList(
    clips.filter((clip) => clip.clipType === "drum" && (sharedMode ? !clip.owned : clip.owned)),
    state.loadModalSortKey,
    state.loadModalSortDirection,
  );
  if (!sortedClips.some((clip) => clip.id === state.loadModalSelectedClipId)) {
    state.loadModalSelectedClipId = null;
  }
  els.loadClipMessage.textContent = message || (sortedClips.length ? "" : `No ${sharedMode ? "shared" : "owned"} drum patterns yet.`);
  els.loadClipList.replaceChildren(...sortedClips.map((clip) => {
    const row = clipListButton(clip, () => selectLoadModalClip(clip.id), clip.id === state.loadModalSelectedClipId, {
      ownerMode: sharedMode ? "bard" : "access",
      asDiv: true,
      showPreview: true,
      showStats: true,
      useSelectedClass: true,
    });
    row.querySelector("a")?.addEventListener("click", (event) => event.stopPropagation());
    row.addEventListener("dblclick", () => loadClipFromServer(clip.id));
    return row;
  }));
  renderLoadModalState();
  renderLoadSortHeaders();
}

function renderLoadModalState() {
  if (!els.confirmLoadClipButton) {
    return;
  }

  els.confirmLoadClipButton.disabled = state.loadingClips || !state.loadModalSelectedClipId;
}

function selectLoadModalClip(clipId) {
  if (state.loadingClips) {
    return;
  }

  const clip = state.loadModalClips.find((candidate) => candidate.id === clipId);
  if (!clip) {
    return;
  }

  state.loadModalSelectedClipId = clip.id;
  els.loadClipList.querySelectorAll(".clip-list-row[data-clip-id]").forEach((row) => {
    row.classList.toggle("is-selected", row.dataset.clipId === clip.id);
  });
  renderLoadModalState();
  previewLoadModalClip(clip);
}

function loadSelectedClipFromModal() {
  if (!state.loadModalSelectedClipId || state.loadingClips) {
    return;
  }

  loadClipFromServer(state.loadModalSelectedClipId);
}

async function previewLoadModalClip(serverClip) {
  const previewRequestId = makeId("load_preview");
  state.loadPreviewRequestId = previewRequestId;
  cancelLoadClipPreview({ keepRequest: true });

  try {
    await Tone.start();
    const clip = normalizeClip(serverClip.clip || serverClip, state.lastKitSize);
    const originalClip = state.clip;
    state.clip = clip;
    try {
      await ensureLaneSamplers();
    } finally {
      state.clip = originalClip;
    }
    if (state.loadPreviewRequestId !== previewRequestId) {
      return;
    }

    const soundfontId = normalizeSoundfontId(clip.soundBank);
    const stepSeconds = (60 / clip.bpm * 4) / clip.stepsPerBar;
    const visibleSemitones = new Set(kitLaneSemitones(clip.kitSize, clip, clip.soundBank));
    for (const note of clip.notes.filter((item) => visibleSemitones.has(item.semitone))) {
      const setting = laneSettingForClip(clip, note.semitone);
      const samplerState = state.laneSamplers.get(`${soundfontId}:${note.semitone}:${setting.midiNote}`);
      const timeoutId = window.setTimeout(() => {
        const frequency = Tone.Frequency(setting.midiNote + (pitchOffsetCents(setting) / 100), "midi").toFrequency();
        const duration = drumNoteDurationSeconds(clip, setting, stepSeconds);
        const velocity = ACCENT_VELOCITIES[normalizeAccent(note.accent)] * (samplerState?.config.defaultVelocity || 0.8);
        samplerState?.sampler?.triggerAttackRelease(frequency, duration, Tone.now(), velocity);
        state.loadPreviewTimeoutIds = state.loadPreviewTimeoutIds.filter((id) => id !== timeoutId);
      }, Math.max(0, note.step * stepSeconds * 1000));
      state.loadPreviewTimeoutIds.push(timeoutId);
    }
  } catch (error) {
    if (state.loadPreviewRequestId === previewRequestId) {
      els.loadClipMessage.textContent = error.message || "Could not preview that drum pattern.";
    }
  }
}

function cancelLoadClipPreview(options = {}) {
  for (const id of state.loadPreviewTimeoutIds) {
    window.clearTimeout(id);
  }
  state.loadPreviewTimeoutIds = [];
  if (!options.keepRequest) {
    state.loadPreviewRequestId = null;
  }
  state.laneSamplers.forEach((samplerState) => samplerState.sampler?.releaseAll?.());
}

function renderClipListStats(clip) {
  const thumbs = Math.max(0, Math.trunc(Number(clip.thumbsUp) || 0));
  const uses = Math.max(0, Math.trunc(Number(clip.usedCount) || 0));
  const score = Math.max(0, Math.trunc(Number(clip.score) || thumbs + uses));
  return `
    <span class="clip-list-stats" aria-label="${thumbs} thumbs, ${uses} uses, score ${score}">
      <span title="Thumbs">${thumbs} thumbs</span>
      <span title="Uses">${uses} uses</span>
      <span title="Score">${score} score</span>
    </span>
  `;
}

function renderClipMiniPreview(serverClip) {
  const clip = normalizeClip(serverClip.clip || serverClip, state.lastKitSize);
  const totalSteps = Math.max(1, clip.bars * clip.stepsPerBar);
  const laneCount = DRUM_LANES.length;
  const notes = clip.notes.map((note) => {
    const left = (note.step / totalSteps) * 100;
    const width = (Math.max(1, note.durationSteps) / totalSteps) * 100;
    const top = ((laneCount - note.semitone - 1) / laneCount) * 100;
    const height = Math.max(10, 100 / laneCount);
    const color = noteColors[note.semitone % noteColors.length];
    return `<span class="clip-list-preview-note" style="left:${left}%;width:${width}%;top:${top}%;height:${height}%;--note-color:${color};"></span>`;
  }).join("");

  return `<span class="clip-list-preview is-drum-preview" aria-hidden="true">${notes}</span>`;
}

function renderLoadModeTabs() {
  const shared = state.loadModalMode === "shared";
  els.loadClipOwnedTab.classList.toggle("active", !shared);
  els.loadClipOwnedTab.setAttribute("aria-selected", shared ? "false" : "true");
  els.loadClipSharedTab.classList.toggle("active", shared);
  els.loadClipSharedTab.setAttribute("aria-selected", shared ? "true" : "false");
}

function sortClipsForList(clips, sortKey, sortDirection) {
  const direction = sortDirection === "asc" ? 1 : -1;
  const valueForClip = (clip) => {
    if (sortKey === "clipType") {
      return "Drum";
    }
    if (sortKey === "soundBank") {
      return labelForSoundBank(clip.soundBank);
    }
    if (sortKey === "updatedAt") {
      return new Date(clip.updatedAt).getTime() || 0;
    }
    if (sortKey === "access") {
      return formatClipAccess(clip);
    }
    if (sortKey === "bardName") {
      return clip.bardName || "Shared";
    }
    return clip.title || DEFAULT_TITLE;
  };

  return [...clips].sort((first, second) => {
    const firstValue = valueForClip(first);
    const secondValue = valueForClip(second);
    if (typeof firstValue === "number" || typeof secondValue === "number") {
      return ((firstValue || 0) - (secondValue || 0)) * direction;
    }

    const compared = String(firstValue).localeCompare(String(secondValue), undefined, { sensitivity: "base" });
    return compared * direction || String(first.id).localeCompare(String(second.id));
  });
}

function renderLoadSortHeaders() {
  document.querySelectorAll("[data-load-sort]").forEach((button) => {
    const active = button.dataset.loadSort === state.loadModalSortKey;
    button.classList.toggle("is-active", active);
    button.setAttribute("aria-sort", active ? (state.loadModalSortDirection === "asc" ? "ascending" : "descending") : "none");
  });
}

function formatClipDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

async function loadClipFromServer(clipId) {
  if (state.loadingClips) {
    return;
  }

  state.loadingClips = true;
  cancelLoadClipPreview();
  renderLoadClipList([], "Loading drum pattern...");

  try {
    const body = await apiRequest(`/api/clips/${encodeURIComponent(clipId)}`);
    const loadedClip = body.clip?.clip;

    if (!loadedClip) {
      throw new Error("The server did not return clip JSON.");
    }

    stopPlayback();
    state.clip = normalizeClip(loadedClip, state.lastKitSize);
    state.lastKitSize = state.clip.kitSize;
    state.serverClipId = body.clip.id ? String(body.clip.id) : null;
    state.serverClipLocked = Boolean(body.clip.locked || !body.clip.owned);
    state.loadedClipStats = normalizeLoadedClipStats(body.clip);
    state.loadedOriginBardName = state.loadedClipStats?.bardName || "";
    state.serverClipSignature = state.serverClipId ? clipServerSignature() : null;
    scheduleStateSave();
    render();
    window.bootstrap.Modal.getOrCreateInstance(els.loadClipModal).hide();
  } catch (error) {
    renderLoadClipList([], error.message || "Could not load that drum pattern.");
  } finally {
    state.loadingClips = false;
  }
}

function renderFileMenuState() {
  els.fileSaveButton.disabled = state.savingClip;
  els.fileSaveButton.textContent = state.savingClip ? "Saving..." : "Save";
  els.fileSaveAsButton.disabled = state.savingClip;
  els.fileSaveAsButton.textContent = state.savingClip ? "Saving..." : "Save As";
  els.unsavedChangesIndicator.classList.toggle("d-none", !hasUnsavedClipChanges());
  renderSaveModalState();
}

function renderTitleWithOrigin() {
  els.clipTitleInput.textContent = state.clip.title || DEFAULT_TITLE;
  els.originBardIndicator.replaceChildren();
  els.originBardIndicator.classList.add("d-none");
  renderClipStats();
}

function renderClipStats() {
  const stats = state.loadedClipStats || (state.serverClipId
    ? {
      id: state.serverClipId,
      shared: Boolean(state.clip.shared),
      stock: Boolean(state.clip.stock),
      usedCount: 0,
      bardName: state.loadedOriginBardName,
    }
    : null);
  els.clipStats.replaceChildren();
  els.clipStats.classList.toggle("d-none", !stats);

  if (!stats) {
    return;
  }

  const accessPill = document.createElement("span");
  accessPill.className = "clip-stat-pill";
  accessPill.textContent = stats.shared ? "Public" : "Private";
  els.clipStats.append(accessPill);

  if (stats.stock) {
    const stockPill = document.createElement("span");
    stockPill.className = "clip-stat-pill";
    stockPill.textContent = "Stock";
    els.clipStats.append(stockPill);
  }

  const usersPill = document.createElement("span");
  usersPill.className = "clip-stat-pill";
  usersPill.textContent = `${stats.usedCount} ${stats.usedCount === 1 ? "user" : "users"}`;
  els.clipStats.append(usersPill);

  if (stats.bardName) {
    const authorPill = document.createElement("span");
    authorPill.className = "clip-stat-pill clip-stat-author";
    const byLabel = document.createElement("span");
    byLabel.textContent = "by";
    authorPill.append(byLabel);
    const link = document.createElement("a");
    link.href = leaderboardBardUrl(stats.bardName);
    link.textContent = stats.bardName;
    authorPill.append(link);
    els.clipStats.append(authorPill);
  }
}

function escapeHtml(value) {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function lanesForClip() {
  return kitLaneSemitones(state.clip.kitSize, state.clip, state.clip.soundBank).map((semitone) => {
    const setting = laneSetting(semitone);
    const label = friendlyDrumName(setting.midiNote);
    return {
      semitone,
      degree: semitone % noteColors.length,
      midiNote: setting.midiNote,
      label,
      color: noteColors[semitone % noteColors.length],
      scaleTone: true,
    };
  });
}

function kitLaneSemitones(kitSize, clip = state.clip, soundBank = clip.soundBank) {
  const normalizedKitSize = effectiveKitSizeForSoundBank(soundBank, kitSize);
  return DRUM_LANES.reduce((semitones, lane, semitone) => {
    if (isLaneAvailableForClip(clip, semitone, soundBank, normalizedKitSize)) {
      semitones.push(semitone);
    }
    return semitones;
  }, []);
}

function labelForLane(semitone) {
  const midi = laneSetting(semitone).midiNote;
  return friendlyDrumName(midi, semitone);
}

function friendlyDrumName(midi, semitone = midi - DEFAULT_BASE_MIDI) {
  return GM_DRUM_NAMES[midi] || DRUM_LANES[semitone]?.label || `Drum ${midi}`;
}

function midiOptionLabel(midi) {
  return `${midi} - ${GM_DRUM_NAMES[midi] || `Drum ${midi}`}`;
}

function isOctaveBoundaryAbove(relativeSemitone) {
  return (relativeSemitone + 1) % 12 === 0;
}

function noteCoveringCell(step, semitone) {
  return state.clip.notes.find((note) => note.semitone === semitone && note.step === step);
}

function setNoteAt(step, lane, accent = NORMAL_ACCENT) {
  const existing = noteCoveringCell(step, lane.semitone);
  let note = existing;

  if (!note) {
    note = {
      id: makeId("note"),
      step,
      semitone: lane.semitone,
      degree: lane.degree,
      durationSteps: 1,
      accent,
    };
    state.clip.notes.push(note);
  } else {
    note.accent = accent;
    note.durationSteps = 1;
  }

  state.clip.notes.sort((a, b) => a.step - b.step || a.semitone - b.semitone);
  touchClip();
  renderGrid();
  renderSummary();
  previewNote(note);
}

function toggleNote(step, lane, event = null) {
  const existing = noteCoveringCell(step, lane.semitone);
  const requestedAccent = event?.shiftKey ? LOUD_ACCENT : (event?.ctrlKey || event?.metaKey ? GHOST_ACCENT : NORMAL_ACCENT);

  if (existing && requestedAccent === NORMAL_ACCENT) {
    state.clip.notes = state.clip.notes.filter((note) => note.id !== existing.id);
    touchClip();
    renderGrid();
    renderSummary();
    return;
  }

  setNoteAt(step, lane, requestedAccent);
}

function render() {
  renderTitleWithOrigin();
  els.bpmInput.value = state.clip.bpm;
  els.soundBankSelect.value = normalizeSoundfontId(state.clip.soundBank);
  els.sustainInput.value = `${clampNumber(state.clip.sustain, 0, 1, DEFAULT_SUSTAIN)}`;
  renderGrid();
  renderGridSettings();
  renderSummary();
  renderFileMenuState();
  renderAdminControls();
  renderReturnControls();
}

function renderSummary() {
  els.doubleButton.disabled = false;
  els.doubleButton.textContent = state.clip.bars > 1 ? "Un-Double" : "Double";
  els.bpmDisplay.textContent = `${state.clip.bpm} BPM`;
  els.bpmDisplay.setAttribute("aria-valuenow", `${state.clip.bpm}`);
  renderPlayButton();
}

function renderPlayButton() {
  els.playButton.innerHTML = "<span class=\"play-icon\" aria-hidden=\"true\"></span>";
  els.playButton.setAttribute("aria-label", state.playing ? "Stop" : "Play");
  els.playButton.setAttribute("title", state.playing ? "Stop" : "Play");
}

function renderReturnControls() {
  els.backToProgressionButton.classList.toggle("d-none", !state.returnHandoff);
}

function renderGrid() {
  const lanes = lanesForClip();
  const totalSteps = totalClipSteps();
  const isBaseKit = !isDrumMachineSoundfont(state.clip.soundBank);
  els.clipGrid.style.setProperty("--step-count", totalSteps);

  const fragment = document.createDocumentFragment();
  const corner = document.createElement("div");
  corner.className = `grid-corner${isBaseKit ? " is-base-kit" : ""}`;
  const gridSettingsButton = document.createElement("button");
  gridSettingsButton.className = "grid-corner-cog-button";
  gridSettingsButton.type = "button";
  gridSettingsButton.innerHTML = "<span aria-hidden=\"true\">&#9881;</span>";
  gridSettingsButton.setAttribute("aria-label", "Drum pattern settings");
  gridSettingsButton.addEventListener("click", showClipSettingsModal);
  corner.append(gridSettingsButton);

  if (isBaseKit) {
    const currentKitSize = validKitSize(state.clip.kitSize);
    const kitButtons = [
      ["small", "SM"],
      ["medium", "MD"],
      ["large", "LG"],
      ["huge", "XL"],
    ];

    for (const [kitSize, label] of kitButtons) {
      const kitButton = document.createElement("button");
      kitButton.className = [
        "grid-corner-kit-button",
        currentKitSize === kitSize ? "is-active" : "",
      ].filter(Boolean).join(" ");
      kitButton.type = "button";
      kitButton.textContent = label;
      kitButton.setAttribute("aria-label", `Load ${kitSize} drum kit`);
      kitButton.setAttribute("aria-pressed", currentKitSize === kitSize ? "true" : "false");
      kitButton.addEventListener("click", () => setKitSize(kitSize));
      corner.append(kitButton);
    }
  }

  fragment.append(corner);

  for (let step = 0; step < totalSteps; step += 1) {
    const header = document.createElement("div");
    header.className = [
      "step-header",
      step === totalSteps - 1 ? "is-grid-end" : "",
      step % state.clip.stepsPerBar === 0 ? "is-bar-start" : "",
      isBeatBoundaryStep(step) ? "is-beat-boundary" : "",
      ...stepSectionClasses(step),
    ].filter(Boolean).join(" ");
    header.textContent = `${(step % state.clip.stepsPerBar) + 1}`;
    fragment.append(header);
  }

  for (const [laneIndex, lane] of lanes.entries()) {
    const label = document.createElement("div");
    const octaveBoundaryClass = isOctaveBoundaryAbove(lane.semitone) ? " is-octave-boundary" : "";
    const laneShadeClass = laneIndex % 2 === 0 ? "is-even-lane" : "is-odd-lane";
    label.className = `lane-label${octaveBoundaryClass} ${laneShadeClass}`;
    label.style.setProperty("--note-color", lane.color);
    label.append(createLaneLabelText(lane), createLaneSettingsArea(lane));
    label.title = midiOptionLabel(lane.midiNote);
    fragment.append(label);

    for (let step = 0; step < totalSteps; step += 1) {
      const note = noteCoveringCell(step, lane.semitone);
      const cell = document.createElement("button");
      cell.className = [
        "note-cell",
        note ? "is-active" : "",
        note ? `is-${normalizeAccent(note.accent)}-accent` : "",
        note ? "is-note-start" : "",
        note ? "is-note-end" : "",
        step % state.clip.stepsPerBar === 0 ? "is-bar-start" : "",
        isBeatBoundaryStep(step) ? "is-beat-boundary" : "",
        octaveBoundaryClass ? "is-octave-boundary" : "",
        laneShadeClass,
        ...stepSectionClasses(step),
      ].filter(Boolean).join(" ");
      cell.type = "button";
      cell.dataset.step = `${step}`;
      cell.dataset.semitone = `${lane.semitone}`;
      cell.style.setProperty("--note-color", lane.color);
      cell.setAttribute("aria-label", `${note ? "Remove" : "Add"} ${lane.label} at step ${step + 1}`);
      cell.addEventListener("click", (event) => {
        if (state.longPressHandled) {
          state.longPressHandled = false;
          return;
        }
        toggleNote(step, lane, event);
      });
      cell.addEventListener("pointerdown", (event) => startLongPress(event, step, lane));
      cell.addEventListener("pointerup", clearLongPress);
      cell.addEventListener("pointercancel", clearLongPress);
      cell.addEventListener("pointerleave", clearLongPress);
      fragment.append(cell);
    }
  }

  const footerCorner = document.createElement("div");
  footerCorner.className = "grid-footer-corner";
  footerCorner.setAttribute("aria-hidden", "true");
  fragment.append(footerCorner);

  for (let step = 0; step < totalSteps; step += 1) {
    const footer = document.createElement("div");
    footer.className = [
      "step-footer",
      step === totalSteps - 1 ? "is-grid-end" : "",
      step % state.clip.stepsPerBar === 0 ? "is-bar-start" : "",
      isBeatBoundaryStep(step) ? "is-beat-boundary" : "",
      ...stepSectionClasses(step),
    ].filter(Boolean).join(" ");
    footer.setAttribute("aria-hidden", "true");
    fragment.append(footer);
  }

  const playhead = document.createElement("div");
  playhead.className = `clip-playhead${state.playing ? " is-visible" : ""}`;
  playhead.setAttribute("aria-hidden", "true");
  fragment.append(playhead);

  els.clipGrid.replaceChildren(fragment);
  updatePlayheadPosition();
}

function isBeatBoundaryStep(step) {
  const barStep = step % state.clip.stepsPerBar;
  const stepsPerBeat = state.clip.stepsPerBar / 4;
  return barStep > 0 && barStep % stepsPerBeat === 0;
}

function stepSectionClasses(step) {
  const barStep = step % state.clip.stepsPerBar;
  const stepsPerBeat = state.clip.stepsPerBar / 4;
  const sectionStep = barStep % stepsPerBeat;
  const sectionIndex = Math.floor(barStep / stepsPerBeat);

  return [
    sectionIndex % 2 === 0 ? "is-dark-step-section" : "is-less-dark-step-section",
    sectionStep === 0 ? "is-step-section-start" : "",
    sectionStep === stepsPerBeat - 1 ? "is-step-section-end" : "",
  ].filter(Boolean);
}

function createLaneSettingsArea(lane) {
  const setting = laneSetting(lane.semitone);
  const wrap = document.createElement("div");
  wrap.className = "lane-settings-area";
  wrap.addEventListener("click", (event) => event.stopPropagation());

  const settingsButton = document.createElement("button");
  settingsButton.className = "lane-cog-button";
  settingsButton.type = "button";
  settingsButton.innerHTML = "<span aria-hidden=\"true\">&#9881;</span>";
  settingsButton.setAttribute("aria-label", `${lane.label} settings`);
  settingsButton.addEventListener("click", () => showLaneSettingsModal(lane.semitone));

  const volume = document.createElement("input");
  volume.className = "lane-volume-slider";
  volume.type = "range";
  volume.min = "0";
  volume.max = "1";
  volume.step = "0.01";
  volume.value = `${setting.volume}`;
  volume.setAttribute("aria-label", `${lane.label} volume`);
  volume.addEventListener("input", () => setLaneVolume(lane.semitone, volume.value));

  const muteButton = document.createElement("button");
  muteButton.className = `lane-mute-button${setting.muted ? " is-muted" : ""}`;
  muteButton.type = "button";
  muteButton.textContent = "M";
  muteButton.setAttribute("aria-pressed", setting.muted ? "true" : "false");
  muteButton.setAttribute("aria-label", `${setting.muted ? "Unmute" : "Mute"} ${lane.label}`);
  muteButton.addEventListener("click", () => toggleLaneMute(lane.semitone));

  const previewButton = document.createElement("button");
  previewButton.className = "lane-preview-button";
  previewButton.type = "button";
  previewButton.innerHTML = "<span aria-hidden=\"true\">&#9658;</span>";
  previewButton.setAttribute("aria-label", `Preview ${lane.label}`);
  previewButton.addEventListener("click", () => previewLane(lane.semitone));

  wrap.append(settingsButton, volume, muteButton, previewButton);
  return wrap;
}

function createLaneLabelText(lane) {
  const text = document.createElement("span");
  text.className = "lane-label-text";
  text.textContent = lane.label;
  return text;
}

function renderGridSettings() {
  document.querySelectorAll("[name='gridResolution']").forEach((input) => {
    input.checked = Number(input.value) === state.clip.stepsPerBar;
  });
  const isBaseKit = !isDrumMachineSoundfont(state.clip.soundBank);
  els.kitSizeField?.classList.toggle("d-none", !isBaseKit);
  els.kitSizeSelect.disabled = !isBaseKit;
  els.kitSizeSelect.value = validKitSize(state.clip.kitSize);
  els.sustainInput.value = `${clampNumber(state.clip.sustain, 0, 1, DEFAULT_SUSTAIN)}`;
}

function totalClipSteps() {
  return state.clip.bars * state.clip.stepsPerBar;
}

function startLongPress(event, step, lane) {
  if (event.pointerType === "mouse") {
    return;
  }

  clearLongPress();
  state.longPressHandled = false;
  state.longPressTimer = window.setTimeout(() => {
    state.longPressHandled = true;
    showNoteAccentModal(step, lane.semitone);
  }, 520);
}

function clearLongPress() {
  if (!state.longPressTimer) {
    return;
  }
  window.clearTimeout(state.longPressTimer);
  state.longPressTimer = null;
}

function showNoteAccentModal(step, semitone) {
  state.selectedNoteCell = { step, semitone };
  const lane = lanesForClip().find((candidate) => candidate.semitone === semitone);
  els.noteAccentTitle.textContent = `${lane?.label || "Drum"} - Step ${step + 1}`;
  document.querySelectorAll("[data-note-accent]").forEach((button) => {
    const accent = normalizeAccent(button.dataset.noteAccent);
    button.classList.toggle("active", noteCoveringCell(step, semitone)?.accent === accent);
  });
  window.bootstrap.Modal.getOrCreateInstance(els.noteAccentModal).show();
}

function setSelectedNoteAccent(accent) {
  if (!state.selectedNoteCell) {
    return;
  }

  const lane = lanesForClip().find((candidate) => candidate.semitone === state.selectedNoteCell.semitone);
  if (!lane) {
    return;
  }

  setNoteAt(state.selectedNoteCell.step, lane, normalizeAccent(accent));
  window.bootstrap.Modal.getOrCreateInstance(els.noteAccentModal).hide();
}

function removeSelectedNote() {
  if (!state.selectedNoteCell) {
    return;
  }

  state.clip.notes = state.clip.notes.filter((note) => (
    note.step !== state.selectedNoteCell.step || note.semitone !== state.selectedNoteCell.semitone
  ));
  state.selectedNoteCell = null;
  touchClip();
  renderGrid();
  renderSummary();
  window.bootstrap.Modal.getOrCreateInstance(els.noteAccentModal).hide();
}

function laneSetting(semitone) {
  const key = `${semitone}`;
  if (!state.clip.laneSettings[key]) {
    state.clip.laneSettings[key] = createDefaultLaneSetting(semitone);
  }
  return state.clip.laneSettings[key];
}

function toggleLaneMute(semitone) {
  const setting = laneSetting(semitone);
  setting.muted = !setting.muted;
  touchClip();
  renderGrid();
}

function setLaneVolume(semitone, value) {
  const setting = laneSetting(semitone);
  setting.volume = clampNumber(value, 0, 1, setting.volume);
  updateLaneSamplerSettings(semitone);
  touchClip();
}

function disposeLaneSampler(semitone) {
  const soundfontId = normalizeSoundfontId(state.clip.soundBank);
  for (const [key, samplerState] of state.laneSamplers.entries()) {
    if (!key.startsWith(`${soundfontId}:${semitone}:`)) {
      continue;
    }
    samplerState.sampler?.dispose?.();
    samplerState.filter?.dispose?.();
    samplerState.gain?.dispose?.();
    state.laneSamplers.delete(key);
  }
}

function showLaneSettingsModal(semitone) {
  state.selectedLaneSemitone = semitone;
  const setting = laneSetting(semitone);
  populateMidiNoteSelect(setting.midiNote);
  els.laneSettingsTitle.textContent = `${labelForLane(semitone)} Settings`;
  els.laneVolumeInput.value = `${setting.volume}`;
  els.laneToneInput.value = `${setting.tone}`;
  els.laneDecayInput.value = `${setting.decay}`;
  els.lanePitchStepsInput.value = `${setting.pitchSteps}`;
  els.lanePitchCentsInput.value = `${setting.pitchCents}`;
  els.laneMidiNoteSelect.value = `${setting.midiNote}`;
  window.bootstrap.Modal.getOrCreateInstance(els.laneSettingsModal).show();
}

function updateSelectedLaneSetting() {
  if (state.selectedLaneSemitone === null) {
    return;
  }

  const setting = laneSetting(state.selectedLaneSemitone);
  const previousMidiNote = setting.midiNote;
  setting.volume = clampNumber(els.laneVolumeInput.value, 0, 1, setting.volume);
  setting.tone = clampNumber(els.laneToneInput.value, 0, 1, setting.tone);
  setting.decay = clampNumber(els.laneDecayInput.value, 0, 1, setting.decay);
  setting.pitchSteps = Math.trunc(clampNumber(els.lanePitchStepsInput.value, -24, 24, setting.pitchSteps));
  setting.pitchCents = Math.trunc(clampNumber(els.lanePitchCentsInput.value, -100, 100, setting.pitchCents));
  const availableMidiNotes = availableMidiNotesForSoundfont();
  const nextMidiNote = Math.trunc(clampNumber(els.laneMidiNoteSelect.value, 35, 81, setting.midiNote));
  setting.midiNote = availableMidiNotes.has(nextMidiNote) ? nextMidiNote : setting.midiNote;
  els.laneMidiNoteSelect.value = `${setting.midiNote}`;
  if (setting.midiNote !== previousMidiNote) {
    disposeLaneSampler(state.selectedLaneSemitone);
    state.audioWarmPromise = null;
  }
  updateLaneSamplerSettings(state.selectedLaneSemitone);
  touchClip();
  renderGrid();
}

function updateSelectedLaneSettingWithPreview() {
  updateSelectedLaneSetting();
  previewSelectedLane();
}

function setTitleFromInput() {
  const nextTitle = state.clip.title.trim().slice(0, 160) || DEFAULT_TITLE;
  if (nextTitle === state.clip.title) {
    renderTitleWithOrigin();
    renderFileMenuState();
    return;
  }

  state.clip.title = nextTitle;
  renderTitleWithOrigin();
  touchClip();
  renderFileMenuState();
}

function setBpmFromInput() {
  state.clip.bpm = Math.trunc(clampNumber(els.bpmInput.value, MIN_BPM, MAX_BPM, 120));
  els.bpmInput.value = state.clip.bpm;
  touchClip();
  renderSummary();
}

function setBpmFromDisplay() {
  const bpm = Number.parseInt(els.bpmDisplay.textContent, 10);
  state.clip.bpm = Math.trunc(clampNumber(bpm, MIN_BPM, MAX_BPM, state.clip.bpm));
  els.bpmInput.value = state.clip.bpm;
  touchClip();
  renderSummary();
}

function setSustainFromInput() {
  state.clip.sustain = clampNumber(els.sustainInput.value, 0, 1, state.clip.sustain);
  els.sustainInput.value = `${state.clip.sustain}`;
  touchClip();
}

function unavailableNoteCountForSelection(soundBank, kitSize) {
  return state.clip.notes.filter((note) => !isLaneAvailableForClip(state.clip, note.semitone, soundBank, kitSize)).length;
}

function confirmPruneUnavailableLanes(soundBank, kitSize) {
  const unavailableCount = unavailableNoteCountForSelection(soundBank, kitSize);
  if (unavailableCount === 0) {
    return true;
  }

  const soundBankLabel = labelForSoundBank(soundBank);
  const noteLabel = unavailableCount === 1 ? "note" : "notes";
  return window.confirm(
    `${soundBankLabel} does not include ${unavailableCount} ${noteLabel} from this pattern. Switching will remove those unavailable lanes.`
  );
}

function pruneUnavailableLanes(soundBank, kitSize) {
  state.clip.notes = state.clip.notes.filter((note) => isLaneAvailableForClip(state.clip, note.semitone, soundBank, kitSize));
}

async function setSoundBankFromInput() {
  const nextSoundBank = normalizeSoundfontId(els.soundBankSelect.value, state.clip.soundBank);
  const nextKitSize = isDrumMachineSoundfont(nextSoundBank) ? "huge" : state.clip.kitSize;
  if (nextSoundBank === state.clip.soundBank) {
    return;
  }
  if (!confirmPruneUnavailableLanes(nextSoundBank, nextKitSize)) {
    els.soundBankSelect.value = normalizeSoundfontId(state.clip.soundBank);
    return;
  }

  const wasPlaying = state.playing;
  const changeToken = state.soundBankChangeToken + 1;
  state.soundBankChangeToken = changeToken;
  clearScheduledTimeouts();
  pruneUnavailableLanes(nextSoundBank, nextKitSize);
  state.clip.soundBank = nextSoundBank;
  playSoundfontPreview("drums", state.clip.soundBank);
  state.clip.kitSize = nextKitSize;
  state.lastKitSize = state.clip.kitSize;
  disposeLaneSamplers();
  state.audioWarmPromise = null;
  touchClip();
  populateMidiNoteSelect();
  renderGridSettings();
  renderGrid();
  renderFileMenuState();

  if (!wasPlaying) {
    warmAudioSoon();
    return;
  }

  try {
    await ensureSynth();
  } catch (error) {
    window.alert(error.message || "Could not load that sound bank.");
    stopPlayback();
    return;
  }

  if (state.playing && state.soundBankChangeToken === changeToken) {
    scheduleClipCycle(currentCycleStartedAt());
  }
}

function setKitSizeFromInput() {
  setKitSize(els.kitSizeSelect.value);
}

function setKitSize(kitSize) {
  if (isDrumMachineSoundfont(state.clip.soundBank)) {
    state.clip.kitSize = "huge";
    renderGridSettings();
    renderGrid();
    return;
  }

  const nextKitSize = validKitSize(kitSize, state.clip.kitSize);
  if (nextKitSize === state.clip.kitSize) {
    return;
  }
  if (!confirmPruneUnavailableLanes(state.clip.soundBank, nextKitSize)) {
    els.kitSizeSelect.value = validKitSize(state.clip.kitSize);
    return;
  }

  pruneUnavailableLanes(state.clip.soundBank, nextKitSize);
  state.clip.kitSize = nextKitSize;
  state.lastKitSize = state.clip.kitSize;
  state.audioWarmPromise = null;
  touchClip();
  renderGridSettings();
  renderGrid();
}

function showClipSettingsModal() {
  renderGridSettings();
  window.bootstrap.Modal.getOrCreateInstance(els.clipSettingsModal).show();
}

function setGridResolutionFromInput(event) {
  state.clip.stepsPerBar = validStepsPerBar(event.target.value, state.clip.stepsPerBar);
  state.clip.notes = normalizeNotes(state.clip.notes, state.clip.bars, state.clip.stepsPerBar);
  touchClip();
  renderGridSettings();
  renderGrid();
}

function doubleClip() {
  if (state.clip.bars > 1) {
    state.clip.bars = 1;
    state.clip.notes = normalizeNotes(state.clip.notes, state.clip.bars, state.clip.stepsPerBar);
    touchClip();
    renderGrid();
    renderSummary();
    return;
  }

  if (state.clip.bars >= MAX_BARS) {
    return;
  }

  const oldTotalSteps = totalClipSteps();
  const oldNotes = state.clip.notes.map((note) => ({ ...note }));
  state.clip.bars *= 2;
  state.clip.notes.push(...oldNotes.map((note) => ({
    ...note,
    id: makeId("note"),
    step: note.step + oldTotalSteps,
    durationSteps: 1,
  })));
  state.clip.notes = normalizeNotes(state.clip.notes, state.clip.bars, state.clip.stepsPerBar);
  touchClip();
  renderGrid();
  renderSummary();
}

function returnToProgression() {
  if (!state.returnHandoff) {
    return;
  }

  setTitleFromInput();
  syncClipEvents();
  saveState();
  try {
    window.localStorage.setItem(CLIP_EDITOR_RETURN_KEY, JSON.stringify({
      version: 1,
      savedAt: new Date().toISOString(),
      ...state.returnHandoff,
      clip: state.clip,
    }));
    window.localStorage.removeItem(CLIP_EDITOR_HANDOFF_KEY);
  } catch {
    window.alert("Could not return this drum pattern because browser storage is unavailable.");
    return;
  }

  const returnUrl = state.returnHandoff.returnUrl || "/songs";
  state.returnHandoff = null;
  saveState();
  window.location.href = returnUrl;
}

function newClip() {
  if (!confirmDiscardUnsavedClip("Start a new drum pattern")) {
    return;
  }

  stopPlayback();
  state.clip = createDefaultClip(state.lastKitSize || DEFAULT_KIT_SIZE);
  state.pristineUnsavedClipSignature = localClipSignature(state.clip);
  state.serverClipId = null;
  state.serverClipSignature = null;
  state.serverClipLocked = false;
  state.loadedOriginBardName = "";
  state.loadedClipStats = null;
  state.returnHandoff = null;
  scheduleStateSave();
  render();
}

function shareLinkForCurrentClip() {
  if (!state.serverClipId) {
    return "";
  }

  const url = new window.URL(window.location.href);
  url.pathname = "/drums/";
  url.search = "";
  url.hash = "";
  url.searchParams.set("loadClipId", state.serverClipId);
  return url.toString();
}

function showShareModal() {
  const link = shareLinkForCurrentClip();
  const isDirty = hasUnsavedClipChanges();
  els.shareLinkInput.value = link;
  els.copyShareLinkButton.disabled = !link;
  els.shareLinkMessage.textContent = "";
  els.shareLinkHelp.textContent = link
    ? `This deep link opens the saved drum pattern. ${isDirty ? "Save again before sharing if you want this link to include your latest edits. " : ""}For social media, mark the drum pattern shared when you save so other people can open it.`
    : "Save this drum pattern first to create a deep link. For social media, mark it shared when you save so other people can open it.";
  window.bootstrap.Modal.getOrCreateInstance(els.shareModal).show();
}

async function copyShareLink() {
  const link = els.shareLinkInput.value;
  if (!link) {
    els.shareLinkMessage.textContent = "Save this drum pattern first to create a link.";
    return;
  }

  try {
    await navigator.clipboard.writeText(link);
  } catch {
    els.shareLinkInput.select();
    document.execCommand("copy");
  }
  els.shareLinkMessage.textContent = "Link copied.";
}

async function loadLeaderboardClipFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const clipId = params.get("loadClipId");
  if (!clipId) {
    return;
  }

  try {
    const body = await apiRequest(`/api/leaderboard/clips/${encodeURIComponent(clipId)}/use`, {
      method: "POST",
      body: JSON.stringify({}),
    });
    const loadedClip = body.clip?.clip;
    if (!loadedClip) {
      throw new Error("The server did not return clip JSON.");
    }

    stopPlayback();
    state.clip = normalizeClip(loadedClip, state.lastKitSize);
    state.lastKitSize = state.clip.kitSize;
    state.serverClipId = body.clip.id ? String(body.clip.id) : null;
    state.serverClipLocked = Boolean(body.clip.locked || !body.clip.owned);
    state.serverClipSignature = state.serverClipId ? clipServerSignature() : null;
    state.loadedClipStats = normalizeLoadedClipStats(body.clip);
    state.loadedOriginBardName = state.loadedClipStats?.bardName || "";
    state.returnHandoff = null;
    scheduleStateSave();
    render();
    window.history.replaceState({}, "", window.location.pathname);
  } catch (error) {
    window.alert(error.message || "Could not load that drum pattern.");
  }
}

async function ensureSynth() {
  await warmAudio({ startContext: true });
}

async function warmAudio(options = {}) {
  if (options.startContext) {
    await Tone.start();
  }

  if (!state.audioWarmPromise) {
    state.audioWarmPromise = ensureLaneSamplers().catch((error) => {
      state.audioWarmPromise = null;
      throw error;
    });
  }

  await state.audioWarmPromise;
}

function warmAudioSoon() {
  const warm = () => {
    warmAudio().catch(() => {
      // Playback and preview still surface load errors when the user asks for audio.
    });
  };

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(warm, { timeout: 1000 });
    return;
  }

  window.setTimeout(warm, 250);
}

async function ensureLaneSamplers() {
  await Promise.all(kitLaneSemitones(state.clip.kitSize, state.clip, state.clip.soundBank).map((semitone) => ensureLaneSampler(semitone)));
}

async function ensureLaneSampler(semitone) {
  const soundfontId = normalizeSoundfontId(state.clip.soundBank);
  const setting = laneSetting(semitone);
  const key = `${soundfontId}:${semitone}:${setting.midiNote}`;
  const existing = state.laneSamplers.get(key);
  if (existing?.ready) {
    updateLaneSamplerSettings(semitone);
    return existing;
  }
  if (existing?.loadPromise) {
    await existing.loadPromise;
    updateLaneSamplerSettings(semitone);
    return existing;
  }

  const config = soundfontConfig(soundfontId);
  const soundfont = window.MIDI?.Soundfont?.[config.globalName];
  if (!soundfont) {
    throw new Error(`Sound bank is not loaded: ${config.label}`);
  }
  const sampleUrl = soundfontSampleUrlForMidi(soundfont, setting.midiNote);
  if (!sampleUrl) {
    throw new Error(`No sample is available for ${friendlyDrumName(setting.midiNote, semitone)}.`);
  }

  const gain = new Tone.Gain(1);
  const filter = new Tone.Filter(12000, "lowpass");
  filter.connect(gain);
  gain.toDestination();
  const samplerState = {
    config,
    gain,
    filter,
    sampler: null,
    ready: false,
    loadPromise: null,
  };

  samplerState.sampler = new Tone.Sampler({
    urls: { [setting.midiNote]: sampleUrl },
    attack: 0,
    release: config.release,
    onload() {
      samplerState.ready = true;
    },
  }).connect(filter);
  samplerState.loadPromise = Tone.loaded().then(() => {
    samplerState.ready = true;
    updateLaneSamplerSettings(semitone);
    return samplerState;
  });
  state.laneSamplers.set(key, samplerState);
  await samplerState.loadPromise;
  return samplerState;
}

function laneSamplerState(semitone) {
  const setting = laneSetting(semitone);
  return state.laneSamplers.get(`${normalizeSoundfontId(state.clip.soundBank)}:${semitone}:${setting.midiNote}`);
}

function updateLaneSamplerSettings(semitone) {
  const samplerState = laneSamplerState(semitone);
  if (!samplerState) {
    return;
  }

  const setting = laneSetting(semitone);
  samplerState.gain.gain.value = setting.muted ? 0 : setting.volume * samplerState.config.gain;
  samplerState.filter.frequency.value = 700 + (setting.tone * 13500);
}

function disposeLaneSamplers() {
  state.laneSamplers.forEach((samplerState) => {
    samplerState.sampler?.dispose?.();
    samplerState.filter?.dispose?.();
    samplerState.gain?.dispose?.();
  });
  state.laneSamplers.clear();
}

function soundfontSampleUrlForMidi(soundfont, midi) {
  const targetNote = Tone.Frequency(midi, "midi").toNote();
  if (soundfont[targetNote]) {
    return soundfont[targetNote];
  }

  let closest = null;
  for (const [note, url] of Object.entries(soundfont)) {
    const sampleMidi = Tone.Frequency(note).toMidi();
    if (!Number.isFinite(sampleMidi)) {
      continue;
    }
    const distance = Math.abs(sampleMidi - midi);
    if (!closest || distance < closest.distance) {
      closest = { distance, url };
    }
  }
  return closest?.url || null;
}

function pitchOffsetCents(setting) {
  return (setting.pitchSteps * 100) + setting.pitchCents;
}

function midiForNote(note) {
  const setting = laneSetting(note.semitone);
  return setting.midiNote + (pitchOffsetCents(setting) / 100);
}

function secondsPerStep() {
  return (60 / state.clip.bpm * 4) / state.clip.stepsPerBar;
}

function showMidiExportDialog() {
  if (!shouldShowMidiExportNotice()) {
    exportMidi();
    return;
  }

  window.bootstrap.Modal.getOrCreateInstance(els.midiExportModal).show();
}

function confirmMidiExport(options = {}) {
  if (options.remember) {
    rememberMidiExportNoticeDismissed();
  }
  window.bootstrap.Modal.getOrCreateInstance(els.midiExportModal).hide();
  exportMidi();
}

function exportMidi() {
  syncClipEvents();
  const clip = state.clip;
  const midi = createMidiDocument({
    name: clip.title,
    ppq: PPQ,
    bpm: clip.bpm,
    timeSignature: [4, 4],
  });
  const track = addMidiTrack(midi, {
    name: clip.title || DEFAULT_TITLE,
    channel: MIDI_DRUM_CHANNEL,
  });

  eventsForClip(clip).forEach((event) => {
    addMidiNote(track, {
      midi: event.midi,
      ticks: Math.round((event.offsetBeats || 0) * PPQ),
      durationTicks: Math.max(1, Math.round((event.durationBeats || (4 / clip.stepsPerBar)) * PPQ)),
      velocity: event.velocity,
    });
  });

  downloadMidi(midi, clip.title || DEFAULT_TITLE);
}

function drumSustainSeconds(clip = state.clip) {
  return clampNumber(clip.sustain, 0, 1, DEFAULT_SUSTAIN) * MAX_SUSTAIN_SECONDS;
}

function drumNoteDurationSeconds(clip, setting, stepSeconds) {
  return Math.max(0.035, stepSeconds * (0.08 + (setting.decay * 0.9))) + drumSustainSeconds(clip);
}

function durationForNote(note) {
  const setting = laneSetting(note.semitone);
  return drumNoteDurationSeconds(state.clip, setting, secondsPerStep());
}

async function previewNote(note) {
  try {
    await ensureSynth();
  } catch (error) {
    window.alert(error.message || "Could not load that sound bank.");
    return;
  }

  triggerNote(note, Tone.now());
}

async function previewLane(semitone) {
  try {
    await ensureSynth();
  } catch (error) {
    window.alert(error.message || "Could not load that sound bank.");
    return;
  }

  triggerNote({
    semitone,
    accent: LOUD_ACCENT,
  }, Tone.now(), { respectMute: false });
}

function previewSelectedLane() {
  if (state.selectedLaneSemitone === null) {
    return;
  }
  previewLane(state.selectedLaneSemitone);
}

function triggerNote(note, time, options = {}) {
  const samplerState = laneSamplerState(note.semitone);
  const setting = laneSetting(note.semitone);
  const respectMute = options.respectMute !== false;
  if (!samplerState || (respectMute && setting.muted)) {
    return;
  }

  const velocity = ACCENT_VELOCITIES[normalizeAccent(note.accent)] * (samplerState.config.defaultVelocity || 0.8);
  const frequency = Tone.Frequency(midiForNote(note), "midi").toFrequency();
  samplerState.sampler?.triggerAttackRelease(frequency, durationForNote(note), time, velocity);
}

async function playClip() {
  if (state.playing) {
    stopPlayback();
    return;
  }

  try {
    await ensureSynth();
  } catch (error) {
    window.alert(error.message || "Could not load that sound bank.");
    return;
  }

  state.playing = true;
  state.playbackStartedAt = Tone.now() + PLAYBACK_START_DELAY_SECONDS;
  els.playButton.classList.add("active");
  renderPlayButton();

  scheduleClipCycle(state.playbackStartedAt);
  startPlayheadAnimation();
}

function scheduleClipCycle(startedAt) {
  if (!state.playing) {
    return;
  }

  const stepSeconds = secondsPerStep();
  const totalSeconds = totalClipSteps() * stepSeconds;
  const visibleSemitones = new Set(kitLaneSemitones(state.clip.kitSize, state.clip, state.clip.soundBank));
  const now = Tone.now();

  for (const note of state.clip.notes.filter((item) => visibleSemitones.has(item.semitone))) {
    const noteTime = startedAt + note.step * stepSeconds;
    if (noteTime <= now) {
      continue;
    }
    addScheduledTimeout(() => {
      triggerNote(note, noteTime);
    }, secondsUntilScheduleWake(noteTime) * 1000);
  }

  const nextCycleAt = startedAt + totalSeconds;
  addScheduledTimeout(() => {
    scheduleClipCycle(nextCycleAt);
  }, secondsUntilScheduleWake(nextCycleAt) * 1000);
}

function secondsUntilScheduleWake(eventTime) {
  return Math.max(0, eventTime - Tone.now() - SCHEDULE_LOOKAHEAD_SECONDS);
}

function currentCycleStartedAt() {
  if (!state.playbackStartedAt) {
    return Tone.now() + PLAYBACK_START_DELAY_SECONDS;
  }

  const totalSeconds = totalClipSteps() * secondsPerStep();
  if (totalSeconds <= 0) {
    return state.playbackStartedAt;
  }

  const elapsedSeconds = Math.max(0, Tone.now() - state.playbackStartedAt);
  return state.playbackStartedAt + (Math.floor(elapsedSeconds / totalSeconds) * totalSeconds);
}

function clearScheduledTimeouts() {
  for (const id of state.scheduledIds) {
    window.clearTimeout(id);
  }
  state.scheduledIds = [];
}

function addScheduledTimeout(callback, delay) {
  const timeoutId = window.setTimeout(() => {
    state.scheduledIds = state.scheduledIds.filter((id) => id !== timeoutId);
    callback();
  }, delay);
  state.scheduledIds.push(timeoutId);
  return timeoutId;
}

function stopPlayback() {
  clearScheduledTimeouts();
  state.playing = false;
  state.playbackStartedAt = 0;
  stopPlayheadAnimation();
  els.playButton.classList.remove("active");
  renderPlayButton();
  updatePlayheadPosition();
  state.laneSamplers.forEach((samplerState) => samplerState.sampler?.releaseAll?.());
}

function startPlayheadAnimation() {
  stopPlayheadAnimation();

  const animate = () => {
    updatePlayheadPosition();
    if (state.playing) {
      state.playheadAnimationFrame = window.requestAnimationFrame(animate);
    }
  };

  state.playheadAnimationFrame = window.requestAnimationFrame(animate);
}

function stopPlayheadAnimation() {
  if (!state.playheadAnimationFrame) {
    return;
  }

  window.cancelAnimationFrame(state.playheadAnimationFrame);
  state.playheadAnimationFrame = null;
}

function updatePlayheadPosition() {
  const playhead = els.clipGrid.querySelector(".clip-playhead");
  if (!playhead) {
    return;
  }

  playhead.classList.toggle("is-visible", state.playing);

  if (!state.playing || !state.playbackStartedAt) {
    els.clipGrid.style.setProperty("--playhead-left", "var(--lane-label-width)");
    return;
  }

  const stepSeconds = secondsPerStep();
  const totalSeconds = totalClipSteps() * stepSeconds;
  const elapsedSeconds = Math.max(0, Tone.now() - state.playbackStartedAt);
  const cycleSeconds = totalSeconds > 0 ? elapsedSeconds % totalSeconds : 0;
  const stepPosition = cycleSeconds / stepSeconds;

  els.clipGrid.style.setProperty(
    "--playhead-left",
    `calc(var(--lane-label-width) + (${stepPosition} * var(--step-width)))`,
  );
}

function tapBpm() {
  const now = performance.now();
  state.tapTimes = state.tapTimes.filter((time) => now - time < 2500);
  state.tapTimes.push(now);

  if (state.tapTimes.length < 2) {
    return;
  }

  const intervals = state.tapTimes.slice(1).map((time, index) => time - state.tapTimes[index]);
  const averageMs = intervals.reduce((sum, interval) => sum + interval, 0) / intervals.length;
  state.clip.bpm = Math.trunc(clampNumber(Math.round(60000 / averageMs), MIN_BPM, MAX_BPM, state.clip.bpm));
  els.bpmInput.value = state.clip.bpm;
  touchClip();
  renderSummary();
}

function handleKeydown(event) {
  const modifierKey = event.ctrlKey || event.metaKey;

  if (modifierKey && event.code === "KeyS") {
    event.preventDefault();
    if (event.repeat) {
      return;
    }
    if (event.shiftKey) {
      showSaveClipModal();
    } else {
      saveClip();
    }
    return;
  }

  if (modifierKey && event.code === "KeyN") {
    event.preventDefault();
    if (!event.repeat) {
      newClip();
    }
    return;
  }

  if (event.code !== "Space" || event.repeat || shouldIgnoreTransportKeydown(event.target)) {
    return;
  }

  event.preventDefault();
  playClip();
}

function shouldIgnoreTransportKeydown(target) {
  if (!(target instanceof window.Element)) {
    return false;
  }

  return Boolean(target.closest("input, select, textarea, button, [contenteditable='true']"));
}

function populateSoundBankSelect() {
  els.soundBankSelect.replaceChildren(...DRUM_SOUNDFONTS.map((soundfont) => {
    const option = document.createElement("option");
    option.value = soundfont.id;
    option.textContent = soundfont.label;
    return option;
  }));
}

function populateMidiNoteSelect(includeMidiNote = null) {
  const availableMidiNotes = availableMidiNotesForSoundfont();
  const midiNotes = Object.keys(GM_DRUM_NAMES)
    .map((midi) => Number(midi))
    .filter((midi) => availableMidiNotes.has(midi));
  const selectedMidiNote = Number(includeMidiNote);
  if (Number.isInteger(selectedMidiNote) && !midiNotes.includes(selectedMidiNote)) {
    midiNotes.push(selectedMidiNote);
  }
  midiNotes.sort((a, b) => a - b);

  els.laneMidiNoteSelect.replaceChildren(...midiNotes.map((midi) => {
    const option = document.createElement("option");
    option.value = `${midi}`;
    option.textContent = midiOptionLabel(midi);
    return option;
  }));
}

function bindEvents() {
  els.fileNewButton.addEventListener("click", newClip);
  els.fileLoadButton.addEventListener("click", showLoadClipModal);
  els.fileSaveButton.addEventListener("click", saveClip);
  els.fileSaveAsButton.addEventListener("click", showSaveClipModal);
  els.fileShareButton.addEventListener("click", showShareModal);
  els.copyShareLinkButton.addEventListener("click", copyShareLink);
  els.fileExportMidiButton.addEventListener("click", showMidiExportDialog);
  els.confirmMidiExportButton.addEventListener("click", () => confirmMidiExport());
  els.midiExportNeverShowButton.addEventListener("click", () => confirmMidiExport({ remember: true }));
  els.backToProgressionButton.addEventListener("click", returnToProgression);
  els.saveClipNameInput.addEventListener("input", () => {
    clearSaveModalSelectionIfEdited();
    renderSaveModalDuplicateState();
  });
  els.saveSharedCheckbox.addEventListener("change", renderSaveSharedInfo);
  document.querySelectorAll("[data-save-sort]").forEach((button) => {
    button.addEventListener("click", () => {
      const key = button.dataset.saveSort || "updatedAt";
      if (state.saveModalSortKey === key) {
        state.saveModalSortDirection = state.saveModalSortDirection === "asc" ? "desc" : "asc";
      } else {
        state.saveModalSortKey = key;
        state.saveModalSortDirection = key === "updatedAt" ? "desc" : "asc";
      }
      renderSaveClipList(state.saveModalClips);
    });
  });
  document.querySelectorAll("[data-load-sort]").forEach((button) => {
    button.addEventListener("click", () => {
      const key = button.dataset.loadSort || "updatedAt";
      if (state.loadModalSortKey === key) {
        state.loadModalSortDirection = state.loadModalSortDirection === "asc" ? "desc" : "asc";
      } else {
        state.loadModalSortKey = key;
        state.loadModalSortDirection = key === "updatedAt" ? "desc" : "asc";
      }
      renderLoadClipList(state.loadModalClips);
    });
  });
  document.querySelectorAll("[data-load-mode]").forEach((button) => {
    button.addEventListener("click", () => {
      state.loadModalMode = button.dataset.loadMode === "shared" ? "shared" : "owned";
      renderLoadClipList(state.loadModalClips);
    });
  });
  els.confirmLoadClipButton?.addEventListener("click", loadSelectedClipFromModal);
  els.loadClipModal.addEventListener("hidden.bs.modal", cancelLoadClipPreview);
  els.confirmSaveClipButton.addEventListener("click", saveClipFromModal);
  els.bpmInput.addEventListener("change", setBpmFromInput);
  els.sustainInput.addEventListener("input", setSustainFromInput);
  els.bpmDisplay.addEventListener("focus", () => {
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(els.bpmDisplay);
    selection.removeAllRanges();
    selection.addRange(range);
  });
  els.bpmDisplay.addEventListener("blur", setBpmFromDisplay);
  els.bpmDisplay.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      els.bpmDisplay.blur();
    }
  });
  els.tapBpmButton.addEventListener("click", tapBpm);
  els.soundBankSelect.addEventListener("change", setSoundBankFromInput);
  els.kitSizeSelect.addEventListener("change", setKitSizeFromInput);
  document.querySelectorAll("[name='gridResolution']").forEach((input) => {
    input.addEventListener("change", setGridResolutionFromInput);
  });
  els.doubleButton.addEventListener("click", doubleClip);
  els.clipSettingsButton.addEventListener("click", showClipSettingsModal);
  els.playButton.addEventListener("click", playClip);
  window.addEventListener("pointerdown", () => {
    warmAudio({ startContext: true }).catch(() => {
      // Playback and preview still surface load errors when the user asks for audio.
    });
  }, { once: true });
  document.querySelectorAll("[data-note-accent]").forEach((button) => {
    button.addEventListener("click", () => setSelectedNoteAccent(button.dataset.noteAccent));
  });
  els.noteAccentRemoveButton.addEventListener("click", removeSelectedNote);
  els.lanePreviewButton.addEventListener("click", previewSelectedLane);
  [
    els.laneVolumeInput,
    els.laneToneInput,
    els.laneDecayInput,
    els.lanePitchStepsInput,
    els.lanePitchCentsInput,
  ].forEach((input) => {
    input.addEventListener("input", updateSelectedLaneSettingWithPreview);
  });
  els.laneMidiNoteSelect.addEventListener("change", updateSelectedLaneSettingWithPreview);
  window.addEventListener("keydown", handleKeydown);
  window.addEventListener("beforeunload", saveState);
}

populateSoundBankSelect();
populateMidiNoteSelect();
loadStoredState();
bindEvents();
render();
loadCurrentUser();
loadLeaderboardClipFromUrl();
warmAudioSoon();
